import { PushAutomationRequest } from "api/PushAutomationRequest";

export type Channel =
  | "APP POR PROGRAMA"
  | "APP BRAZIL"
  | "APP BOTNAR"
  | "AFINIAPP"
  | "WHATSAPP POR PROGRAMA"
  | "WHATSAPP BRAZIL"
  | "WHATSAPP BOTNAR";

export const CHANNELS: Channel[] = [
  "APP POR PROGRAMA",
  "APP BRAZIL",
  "APP BOTNAR",
  "AFINIAPP",
  "WHATSAPP POR PROGRAMA",
  "WHATSAPP BRAZIL",
  "WHATSAPP BOTNAR",
];

export const WA_CHANNELS: Channel[] = ["WHATSAPP POR PROGRAMA", "WHATSAPP BRAZIL", "WHATSAPP BOTNAR"];

export type OutputType = "Activity" | "Article" | "Session" | "Milestones";
export const OUTPUT_TYPES: OutputType[] = ["Activity", "Article", "Session", "Milestones"];

export interface LocalizedText {
  es: string;
  pt?: string;
  en?: string;
}

export interface MediaSpec {
  type: "TEXT" | "IMAGE" | "VIDEO";
  link?: string;
  // objects.id existente — cuando viene, el backend reusa esa fila tal cual en vez de
  // crear/actualizar una por nombre de template (ver persistWaTemplateLinks).
  object_id?: string;
}

export interface LocalizedMedia {
  es?: MediaSpec;
  pt?: MediaSpec;
  en?: MediaSpec;
}

export interface CreateSendInput {
  channel: Channel;
  scheduled_at: string;
  limit?: number;
  template?: LocalizedText;
  session?: LocalizedText;
  media?: LocalizedMedia;
  output?: OutputType;
  message?: LocalizedText;
  program_ids?: number[];
  user_channel_row_ids?: number[];
  bot_channel_ids?: string[];
  offset?: number;
  order_by?: string;
  send_suppressed?: boolean;
  suppressed_days?: number;
  inactive_range?: boolean;
  age_min?: number;
  age_max?: number;
}

export interface SegmentPreview {
  total: number;
  users: { user_id: number; user_channel: string }[];
  note?: string;
}

export interface CreateSendResult {
  id: string;
  status: string;
  scheduled_at: string;
  preview: SegmentPreview;
}

export interface ListSendsQuery {
  search?: string;
  program_ids?: string;
  channel?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  order_by?: string;
  order_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface SendReport {
  success: number;
  fail: number;
  skipped?: number;
  successes?: number[];
  errors?: number[];
  skippeds?: Record<string, number[]>;
  aborted?: { reason: string; remainingUsers: number; lastError?: unknown };
  dry?: boolean;
  by_group?: Record<string, { success: number; fail: number }>;
  by_bot?: Record<string, { success: number; fail: number }>;
  run_history?: {
    dry_runs: number;
    real_runs: number;
    runs: { ran_at: string; dry: boolean; success: number; fail: number }[];
  };
}

export interface ListSendsRow {
  id: string;
  channel: string;
  program_ids: string[] | null;
  date_to_send: string | null;
  status: string;
  query_params: unknown;
  report: SendReport | null;
  push_name: string | null;
  templates: string[];
  age_min: number | null;
  age_max: number | null;
}

// Shape de query_params tal como lo persiste createScheduledSend (scheduleSend.ts) —
// usado por la vista de solo lectura de configuracion del envio.
export interface SendQueryParams {
  limit?: number | null;
  offset?: number | null;
  orderBy?: string | null;
  userChannels?: number[] | null;
  userChannelRowIds?: number[] | null;
  botChannelIds?: string[] | null;
  sendSuppressed?: boolean | null;
  suppressedDays?: number | null;
  inactiveRange?: boolean | null;
}

export interface ListSendsResult {
  total: number;
  rows: ListSendsRow[];
}

export const createSend = (data: CreateSendInput) => {
  return PushAutomationRequest.post<CreateSendResult>("/sends", data);
};

export const listSends = (query: ListSendsQuery) => {
  return PushAutomationRequest.get<ListSendsResult>("/sends", { params: query });
};

export const getSendDetail = (id: string) => {
  return PushAutomationRequest.get<ListSendsRow>(`/sends/${id}`);
};

export interface CancelSendResult {
  sendId: string;
  status: string;
  message: string;
  cancelledAt: string;
}

export const cancelSend = (id: string) => {
  return PushAutomationRequest.post<CancelSendResult>(`/sends/${id}/cancel`);
};

export interface ExecuteSendResult {
  sendId: string;
  status: string;
  message: string;
}

export const executeSend = (id: string, dry: boolean) => {
  return PushAutomationRequest.post<ExecuteSendResult>(`/sends/${id}/execute`, { dry });
};

export interface ObjectSearchResult {
  id: string;
  name: string;
  url: string | null;
  type: number;
  damaged: boolean;
  has_media_id: boolean;
  media_ids_by_account: { account_id: number; uploaded_at: string }[];
}

export const searchObjects = (q: string) => {
  return PushAutomationRequest.get<ObjectSearchResult[]>("/objects/search", { params: { q } });
};
